/**
 * TD Product Schema
 * Merges Yotpo's review data into the server-rendered Product JSON-LD, then removes
 * Yotpo's own Product JSON-LD, so the page describes one product with price, stock,
 * rating and reviews together.
 *
 * Yotpo's copy is any Product/ProductGroup JSON-LD outside `<td-product-schema>` that
 * carries `aggregateRating` or `review` (this theme renders no other Product schema).
 * Its `aggregateRating` replaces ours and up to 5 of its `review` entries are added.
 * Handles Yotpo injecting its schema after load, and re-merges when a section
 * re-render replaces our JSON-LD.
 * Markup: snippets/td-product-schema.liquid
 */
if (!customElements.get('td-product-schema')) {
  const REVIEW_LIMIT = 5;
  const JSON_LD_SELECTOR = 'script[type="application/ld+json"]';
  const PRODUCT_TYPES = new Set(['Product', 'ProductGroup']);

  /** Review data taken from Yotpo's JSON-LD, kept so it survives re-renders of ours. */
  const yotpoData = {
    /** @type {object | null} */
    aggregateRating: null,
    /** @type {object[]} */
    review: [],
  };

  /** @type {Set<TdProductSchema>} */
  const instances = new Set();

  const toArray = (/** @type {any} */ value) => (Array.isArray(value) ? value : value ? [value] : []);

  const isProductNode = (/** @type {any} */ node) =>
    toArray(node?.['@type']).some((type) => PRODUCT_TYPES.has(type));

  const isYotpoProductNode = (/** @type {any} */ node) =>
    isProductNode(node) && Boolean(node.aggregateRating || node.review);

  /** Drops the nested `@context` so the node fits inside our document. */
  const withoutContext = (/** @type {any} */ node) => {
    const { '@context': _context, ...rest } = node;
    return rest;
  };

  /**
   * Reads review data from Yotpo's Product JSON-LD and removes those Product nodes.
   * Other schema types are left untouched; a mixed array or `@graph` keeps its other nodes.
   */
  const collectYotpoSchemas = () => {
    let found = false;

    for (const script of document.querySelectorAll(JSON_LD_SELECTOR)) {
      if (script.closest('td-product-schema')) continue;

      let data;
      try {
        data = JSON.parse(script.textContent ?? '');
      } catch {
        continue;
      }

      const isList = Array.isArray(data);
      const isGraph = !isList && Array.isArray(data?.['@graph']);
      const nodes = /** @type {any[]} */ (isList ? data : isGraph ? data['@graph'] : [data]);
      const yotpoNodes = nodes.filter(isYotpoProductNode);
      if (!yotpoNodes.length) continue;

      found = true;
      const reviews = [];
      for (const node of yotpoNodes) {
        if (node.aggregateRating) {
          yotpoData.aggregateRating = { '@type': 'AggregateRating', ...withoutContext(node.aggregateRating) };
        }
        for (const review of toArray(node.review)) {
          if (review && typeof review === 'object') reviews.push({ '@type': 'Review', ...withoutContext(review) });
        }
      }
      if (reviews.length) yotpoData.review = reviews.slice(0, REVIEW_LIMIT);

      const kept = nodes.filter((node) => !yotpoNodes.includes(node));
      if (!kept.length) {
        script.remove();
      } else {
        script.textContent = JSON.stringify(isList ? kept : isGraph ? { ...data, '@graph': kept } : kept[0]);
      }
    }

    if (found) {
      for (const instance of instances) instance.merge();
    }
  };

  /** @type {MutationObserver | null} Page-level; Yotpo can inject its schema at any time. */
  let pageObserver = null;

  const watchYotpoSchemas = () => {
    if (pageObserver) return;
    collectYotpoSchemas();

    pageObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(JSON_LD_SELECTOR) || node.querySelector(JSON_LD_SELECTOR)) {
            collectYotpoSchemas();
            return;
          }
        }
      }
    });
    pageObserver.observe(document.documentElement, { childList: true, subtree: true });
  };

  class TdProductSchema extends HTMLElement {
    /** @type {MutationObserver | null} */
    #observer = null;

    connectedCallback() {
      this.#teardown();
      instances.add(this);

      // Re-merge when a section re-render replaces our JSON-LD.
      this.#observer = new MutationObserver(() => this.merge());
      this.#observer.observe(this, { childList: true, subtree: true, characterData: true });

      watchYotpoSchemas();
      this.merge();
    }

    disconnectedCallback() {
      this.#teardown();
    }

    #teardown() {
      instances.delete(this);
      this.#observer?.disconnect();
      this.#observer = null;
    }

    /** Writes the collected Yotpo rating and reviews into our JSON-LD. */
    merge() {
      if (!yotpoData.aggregateRating && !yotpoData.review.length) return;

      const script = this.querySelector(JSON_LD_SELECTOR);
      if (!(script instanceof HTMLScriptElement)) return;

      let schema;
      try {
        schema = JSON.parse(script.textContent ?? '');
      } catch {
        return;
      }

      if (yotpoData.aggregateRating) schema.aggregateRating = yotpoData.aggregateRating;
      if (yotpoData.review.length) schema.review = yotpoData.review;

      const text = JSON.stringify(schema);
      // Our own write triggers the observer; stop once the content is already merged.
      if (text !== script.textContent) script.textContent = text;
    }
  }

  customElements.define('td-product-schema', TdProductSchema);
}
